/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    alpha,
    Tooltip,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { Iconify } from 'src/components';
import { useSelector } from 'react-redux';
import { PATH_DASHBOARD } from 'src/routes/paths';
// Source data is fetched at the page level (AnalyticsReportsBySource) with
// multi-practice analyticsPracticeIds + a _sourceFetchKey dedupe — see the
// parent's useEffect. This component is a pure reader of sourceStatusData,
// hence no fetchSourceStatusData / setSourceStatusData import here.
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { buildDrilldownTitle } from 'src/constants/reportStatusPalette';
import MetricLineChart from './MetricLineChart';
import TreatmentLeadPopup from './TreatmentLeadPopup';
import DataScopeIndicator from './DataScopeIndicator';

const TABLE_NAME = 'Source Status Table';

// All available columns for visibility toggle
const ALL_COLUMNS = [
    { field: 'sourceType', label: 'Source Type' },
    { field: 'enquiries', label: 'Enquiries' },
    { field: 'CONSULTATION_BOOKED', label: 'Consult Booked' },
    { field: 'CONSULTATION_ATTENDED', label: 'Attended' },
    { field: 'TREATMENT_STARTED', label: 'Started' },
    { field: 'CLOSED', label: 'Completed Treatment' },
    { field: 'revenue', label: 'Revenue' },
    { field: 'conversionRate', label: 'Conversion' },
];

// Status palette — kept in lock-step with the lead-status chips / KPI cards:
// blue → purple → amber → green → grey.
const STATUS_COLUMNS = [
    { id: 'CONSULTATION_BOOKED', label: 'Consult Booked', color: '#8b5cf6' },
    { id: 'CONSULTATION_ATTENDED', label: 'Attended', color: '#f59e0b' },
    { id: 'TREATMENT_STARTED', label: 'Started', color: '#10b981' },
    { id: 'CLOSED', label: 'Completed Treatment', color: '#64748b' },
];

// Format currency
const formatCurrency = (value) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
    return `£${value?.toFixed(0) || 0}`;
};

export default function SourceStatusTable({
    practiceId,
    dateRange,
}) {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [view, setView] = useState('table');
    const [sortModel, setSortModel] = useState([{ field: 'enquiries', sort: 'desc' }]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState({
        status: '',
        statusLabel: '',
        sourceType: '',
    });
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    const { sourceStatusData } = useSelector((state) => state.analyticsReports || {});
    const tableData = sourceStatusData?.data || [];
    const isLoading = sourceStatusData?.isLoading || false;

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            dispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    // No internal fetch / cleanup — the parent page dispatches with the full
    // multi-practice analyticsPracticeIds and dedupes on _sourceFetchKey.
    // Previously this component ALSO dispatched with single practiceId, which
    // raced the page-level rollup AND its unmount cleanup nuked the cache.

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!searchQuery) return tableData;
        return tableData.filter((row) =>
            row.sourceType?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [tableData, searchQuery]);

    // Chart data derived from the same filteredData the table uses
    const chartCategories = useMemo(() => filteredData.map((row) => row.sourceType || ''), [filteredData]);

    const chartSeries = useMemo(() =>
        STATUS_COLUMNS
            .filter((status) => !hiddenColumns.includes(status.id))
            .map((status) => ({
                name: status.label,
                data: filteredData.map((row) => row[status.id] || 0),
                color: status.color,
            })),
    [filteredData, hiddenColumns]);

    // Export CSV — exports exactly what the user currently sees (filtered view)
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rows = filteredData.map((row) => visibleCols.map((col) => {
            if (col.field === 'revenue') return formatCurrency(row.revenue || 0);
            if (col.field === 'conversionRate') return `${parseFloat(row.conversionRate) || 0}%`;
            return row[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('source-performance', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, hiddenColumns]);

    // Handle cell click for popup
    const handleCellClick = (row, status) => {
        setPopupData({
            status: status.id,
            statusLabel: status.label,
            sourceType: row.sourceType,
        });
        setPopupOpen(true);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    // Build columns
    const columns = useMemo(() => {
        const cols = [
            {
                field: 'sourceType',
                headerName: 'Source Type',
                flex: 1.3,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                bgcolor: alpha('#3b82f6', 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Iconify
                                icon={getSourceIcon(params.value)}
                                width={18}
                                sx={{ color: '#3b82f6' }}
                            />
                        </Box>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                            {params.value}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'enquiries',
                headerName: 'Enquiries',
                flex: 0.8,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={() => value > 0 && handleCellClick(params.row, { id: 'NEW', label: 'Enquiries' })}
                            sx={{
                                bgcolor: value > 0 ? alpha('#3b82f6', 0.12) : 'transparent',
                                color: value > 0 ? '#3b82f6' : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 ? 'pointer' : 'default',
                                '&:hover': value > 0 ? { bgcolor: alpha('#3b82f6', 0.2) } : {},
                            }}
                        />
                    );
                },
            },
        ];

        // Add status columns
        STATUS_COLUMNS.forEach((status) => {
            cols.push({
                field: status.id,
                headerName: status.label,
                flex: 0.9,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={() => value > 0 && handleCellClick(params.row, status)}
                            sx={{
                                bgcolor: value > 0 ? alpha(status.color, 0.12) : 'transparent',
                                color: value > 0 ? status.color : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 ? 'pointer' : 'default',
                                '&:hover': value > 0 ? {
                                    bgcolor: alpha(status.color, 0.2),
                                } : {},
                            }}
                        />
                    );
                },
            });
        });

        // Add revenue column — clicks drill into completed treatments (CLOSED)
        // since revenue is realised on completion.
        cols.push({
            field: 'revenue',
            headerName: 'Revenue',
            flex: 0.9,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                return (
                    <Chip
                        label={formatCurrency(value)}
                        size="small"
                        onClick={() => value > 0 && handleCellClick(params.row, { id: 'CLOSED', label: 'Revenue' })}
                        sx={{
                            bgcolor: value > 0 ? alpha('#10b981', 0.12) : 'transparent',
                            color: value > 0 ? '#10b981' : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                            cursor: value > 0 ? 'pointer' : 'default',
                            '&:hover': value > 0 ? { bgcolor: alpha('#10b981', 0.2) } : {},
                        }}
                    />
                );
            },
        });

        // Add conversion rate column with Chip
        cols.push({
            field: 'conversionRate',
            headerName: 'Conversion',
            flex: 0.9,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = parseFloat(params.value) || 0;
                const isGood = rate >= 20;
                const color = isGood ? '#10b981' : '#f59e0b';
                return (
                    <Chip
                        label={`${rate}%`}
                        size="small"
                        sx={{
                            bgcolor: alpha(color, 0.12),
                            color,
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        });

        return cols;
    }, []);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            {/* Header with Search and Toolbar */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Tooltip title="Status breakdown showing how leads from each source progress through the pipeline." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            Source Performance
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search source..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Table / Graph view toggle */}
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        size="small"
                        onChange={(_, newView) => { if (newView) setView(newView); }}
                        sx={{ height: 32 }}
                    >
                        <ToggleButton
                            value="table"
                            sx={{
                                px: 1.5,
                                fontSize: '12px',
                                fontWeight: 600,
                                borderRadius: '8px 0 0 8px',
                                border: '1px solid #e2e8f0',
                                ...(view === 'table' && {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                }),
                            }}
                        >
                            <Iconify icon="mdi:table" width={16} sx={{ mr: 0.5 }} />
                            Table
                        </ToggleButton>
                        <ToggleButton
                            value="graph"
                            sx={{
                                px: 1.5,
                                fontSize: '12px',
                                fontWeight: 600,
                                borderRadius: '0 8px 8px 0',
                                border: '1px solid #e2e8f0',
                                ...(view === 'graph' && {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                }),
                            }}
                        >
                            <Iconify icon="mdi:chart-bar" width={16} sx={{ mr: 0.5 }} />
                            Graph
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Schedule email report */}
                    <Tooltip title="Schedule this report via email" arrow>
                        <IconButton
                            size="small"
                            onClick={() => navigate(`${PATH_DASHBOARD.analyticsReports.settings}?prefill=BY_SOURCE_SUMMARY`)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: alpha('#7c3aed', 0.08), borderColor: '#7c3aed' },
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={16} sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            <DataScopeIndicator variant="source" />

            {/* DataGrid / Graph */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {view === 'table' ? (
                        <>
                            <Box>
                                <DataGridPro
                                    rows={filteredData}
                                    columns={columns}
                                    sortModel={sortModel}
                                    onSortModelChange={setSortModel}
                                    columnVisibilityModel={columnVisibilityModel}
                                    autoHeight
                                    disableRowSelectionOnClick
                                    disableColumnMenu={false}
                                    pagination
                                    pageSizeOptions={[5, 10, 25]}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 10 } },
                                    }}
                                    getRowId={(row) => row.id || row.sourceType}
                                    sx={{
                                        border: 'none',
                                        '& .MuiDataGrid-columnHeaders': {
                                            bgcolor: '#f8fafc',
                                            borderBottom: '1px solid #e2e8f0',
                                        },
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderBottom: '1px solid #f1f5f9',
                                        },
                                        '& .MuiDataGrid-row:hover': {
                                            bgcolor: '#f8fafc',
                                        },
                                    }}
                                />
                            </Box>
                            {filteredData.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography sx={{ color: '#64748b' }}>
                                        No source data available
                                    </Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ p: 2.5 }}>
                            <MetricLineChart
                                type="bar"
                                categories={chartCategories}
                                series={chartSeries}
                                height={380}
                                stacked
                                yAxisTitle="Leads"
                            />
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5, textAlign: 'center' }}>
                                Bars show distinct leads that entered each stage during the period. A lead can appear in several stages, so bars do not add up to total leads.
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {/* Lead Popup — unified on TreatmentLeadPopup so the drilldown UI
                matches Overview / By Treatment. sourceTypeFilter scopes the
                lead list client-side after the base fetch. */}
            <TreatmentLeadPopup
                open={popupOpen}
                onClose={handlePopupClose}
                practiceId={practiceId}
                treatmentName={buildDrilldownTitle(popupData.sourceType, popupData.statusLabel, popupData.status, 'All Sources')}
                sourceTypeFilter={popupData.sourceType || ''}
                status={popupData.status}
                dateRange={dateRange ? {
                    startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
                    endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
                } : null}
            />
        </Box>
    );
}

// Helper function to get icon based on source type
function getSourceIcon(sourceType) {
    const type = sourceType?.toLowerCase() || '';
    if (type.includes('google')) return 'mdi:google';
    if (type.includes('facebook') || type.includes('meta')) return 'mdi:facebook';
    if (type.includes('instagram')) return 'mdi:instagram';
    if (type.includes('whatsapp')) return 'mdi:whatsapp';
    if (type.includes('website') || type.includes('web')) return 'mdi:web';
    if (type.includes('phone') || type.includes('call')) return 'mdi:phone';
    if (type.includes('email')) return 'mdi:email';
    if (type.includes('referral')) return 'mdi:account-group';
    if (type.includes('walk')) return 'mdi:walk';
    return 'mdi:source-branch';
}

SourceStatusTable.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateRange: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    }),
};

SourceStatusTable.defaultProps = {
    practiceId: null,
    dateRange: null,
};
